import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StoreBannerUploader from '@/components/settings/StoreBannerUploader';
import { uploadProfileAssetToStorage } from '@/integrations/supabase/storage';

const profileSchema = z.object({
  full_name: z.string().min(2, "Emri duhet të jetë të paktën 2 karaktere"),
  username: z.string().min(3, "Përdoruesi duhet të jetë të paktën 3 karaktere"),
  email: z.string().email("Ju lutemi vendosni një email të vlefshëm"),
  phone_number: z.string().optional(),
  location: z.string().optional(),
  store_name: z.string().optional(),
  store_banner_url: z.union([
    z.string().url({ message: "URL e pavlefshme." }).optional().or(z.literal('')),
    z.instanceof(File).optional(),
    z.null().optional(),
  ]).optional(),
  store_description: z.string().optional(),
});

const passwordSchema = z.object({
  password: z.string().min(6, "Fjalëkalimi duhet të jetë të paktën 6 karaktere"),
  confirmPassword: z.string().min(6, "Fjalëkalimi duhet të jetë të paktën 6 karaktere"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Fjalëkalimet nuk përputhen",
  path: ["confirmPassword"],
});

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

interface ProfileUpdatePayload {
  full_name?: string;
  username?: string;
  phone_number?: string | null;
  location?: string | null;
  store_name?: string | null;
  store_banner_url?: string | null;
  store_description?: string | null;
}


const ProfileSettings = () => {
  const { profile, user, refreshUserProfile, isAuthenticated } = useAuth(); // Added isAuthenticated
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: '',
      username: '',
      email: '',
      phone_number: '',
      location: '',
      store_name: '',
      store_banner_url: null,
      store_description: '',
    },
  });
  
  // Helper function to ensure store_banner_url is always string or null when resetting the form
  const getFormResetValues = (profile: any, user: any, currentValues?: ProfileFormValues): Omit<ProfileFormValues, 'store_banner_url'> & { store_banner_url: string | null } => {
    return {
      full_name: profile?.full_name || '',
      username: profile?.username || '',
      email: user?.email || '',
      phone_number: profile?.phone_number || '',
      location: profile?.location || '',
      store_name: profile?.store_name || '',
      store_banner_url: typeof profile?.store_banner_url === 'string' ? profile?.store_banner_url : null,
      store_description: profile?.store_description || '',
      // Include any other values from currentValues if provided
      ...(currentValues && {
        full_name: currentValues.full_name,
        username: currentValues.username,
        email: currentValues.email,
        phone_number: currentValues.phone_number || '',
        location: currentValues.location || '',
        store_name: currentValues.store_name || '',
        store_description: currentValues.store_description || '',
      }),
    };
  };
  
  useEffect(() => {
    if (profile && user) {
      // Only reset the form if it hasn't been modified by the user
      if (!form.formState.isDirty && !form.formState.isSubmitting) {
        // Use the helper function to ensure correct types
        const resetValues = getFormResetValues(profile, user);
        form.reset(resetValues);
      }
    }
  }, [profile, user, form.formState.isDirty, form.formState.isSubmitting]);

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const handleUpdateProfile = async (values: ProfileFormValues) => {
    if (!user || !profile) return;
    
    setIsLoading(true);
    try {
      let finalBannerUrl: string | null = values.store_banner_url === undefined ? profile.store_banner_url : null;

      const bannerValue = values.store_banner_url;

      if (bannerValue instanceof File) {
        try {
          toast({ title: "Duke Ngarkuar Banerin...", description: "Ju lutemi prisni."});
          // Pass isAuthenticated to the upload function
          finalBannerUrl = await uploadProfileAssetToStorage(bannerValue, user.id, 'banner', isAuthenticated); 
        } catch (uploadError: any) {
          toast({
            title: "Gabim në ngarkimin e banerit",
            description: uploadError.message || "Dështoi ngarkimi i banerit të ri.",
            variant: "destructive",
          });
          setIsLoading(false);
          return; 
        }
      } else if (typeof bannerValue === 'string' && bannerValue.trim() !== '') {
        finalBannerUrl = bannerValue;
      }
      
      const updatePayload: ProfileUpdatePayload = {
        full_name: values.full_name,
        username: values.username,
        phone_number: values.phone_number || null,
        location: values.location || null,
      };

      if (profile.is_store) {
        updatePayload.store_name = values.store_name || null;
        updatePayload.store_banner_url = finalBannerUrl;
        updatePayload.store_description = values.store_description || null;
      }
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update(updatePayload) 
        .eq('id', user.id);

      if (profileError) throw profileError;

      if (values.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: values.email,
        });
        if (emailError) throw emailError;
        toast({
          title: "Përditësimi i emailit u iniciua",
          description: "Ju lutemi kontrolloni inbox-in tuaj për të konfirmuar adresën e re të emailit.",
        });
      }

      toast({
        title: "Profili u përditësua",
        description: "Informacioni i profilit tuaj është përditësuar me sukses.",
      });
      await refreshUserProfile(); 
      
      const resetValues = {
        ...values,
        store_banner_url: finalBannerUrl
      };
      
      form.reset(resetValues);

    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Gabim në përditësimin e profilit",
        description: error.message || "Ndodhi një gabim gjatë përditësimit të profilit tuaj.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (values: PasswordFormValues) => {
    setIsPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (error) throw error;

      toast({
        title: "Fjalëkalimi u përditësua",
        description: "Fjalëkalimi juaj është përditësuar me sukses.",
      });
      passwordForm.reset();
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast({
        title: "Gabim në përditësimin e fjalëkalimit",
        description: error.message || "Ndodhi një gabim gjatë përditësimit të fjalëkalimit.",
        variant: "destructive",
      });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Cilësimet e Profilit</h1>
          
          <Card>
            <CardHeader>
              <CardTitle>Informacioni Juaj</CardTitle>
              <CardDescription>
                Përditësoni informacionin tuaj personal dhe të dhënat e kontaktit.
                {profile?.is_store && " Gjithashtu, menaxhoni detajet e dyqanit tuaj, përfshirë banerin dhe përshkrimin."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleUpdateProfile)} className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="full_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Emri i Plotë</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Përdoruesi</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="phone_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Numri i Telefonit</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Vendndodhja</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {profile?.is_store && user && (
                    <>
                      <FormField
                        control={form.control}
                        name="store_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Emri i Dyqanit</FormLabel>
                            <FormControl>
                              <Input placeholder="Emri i dyqanit tuaj" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="store_banner_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <StoreBannerUploader
                                value={field.value} 
                                onChange={field.onChange} 
                              />
                            </FormControl>
                            <FormMessage /> 
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="store_description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Përshkrimi i Dyqanit</FormLabel>
                            <FormControl>
                              <Textarea placeholder="Përshkruani dyqanin tuaj..." {...field} value={field.value || ''} rows={6} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Duke ruajtur...
                      </>
                    ) : "Ruaj Ndryshimet"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Ndrysho Fjalëkalimin</CardTitle>
              <CardDescription>
                Ndryshoni fjalëkalimin tuaj aktual për llogari më të sigurt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit(handleUpdatePassword)} className="space-y-6">
                  <FormField
                    control={passwordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fjalëkalimi i Ri</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input 
                              type={showPassword ? "text" : "password"} 
                              {...field} 
                            />
                          </FormControl>
                          <button 
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Konfirmo Fjalëkalimin</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input 
                              type={showConfirmPassword ? "text" : "password"} 
                              {...field} 
                            />
                          </FormControl>
                          <button 
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isPasswordLoading}
                  >
                    {isPasswordLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Duke përditësuar...
                      </>
                    ) : "Përditëso Fjalëkalimin"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProfileSettings;
